import {colors} from 'nf-ui'
import {motion} from 'framer-motion'
import React, {useRef, useEffect, useState} from 'react'
import styled from 'styled-components'
import {Profile} from '../useFullOrgChartData'
import {HorizontalProfileItem} from './HorizontalProfileItem'

const TreeContainer = styled.div`
    position: relative;
    min-width: fit-content;
    min-height: fit-content;
    padding-bottom: 32px;
`

const LinesContainer = styled.svg`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;

    line {
        transition: all 0.1s linear;
    }
`

const PROFILE_WIDTH = 120
// Updated height for vertical layout: Photo (32) + margins (8+8) + Line 1 (16) + margin (4) + Line 2 (15) + margin (8) + BadgeContainer (~24) + padding (11*2) ≈ 137px
const PROFILE_HEIGHT = 140 // Approximate height for vertical stacked layout
const HORIZONTAL_SPACING = 30
// Vertical layout:
// - Each level gets a full row height
// - Row height = profile height + vertical gap between rows
const ROW_VERTICAL_GAP = 64
const LINE_WIDTH = 2
const LINE_Y_OFFSET = -4
const ROW_HEIGHT = PROFILE_HEIGHT + ROW_VERTICAL_GAP
const PARENT_LINE_START_ADJUST = 1
const CHILD_LINE_END_ADJUST = 4
const FADE_DURATION_SECONDS = 0.1
const FALLBACK_SHIFT = 0
const LARGE_GAP = 1000000

export type TreeNode = {
    profile: Profile
    x: number
    y: number
    children: TreeNode[]
    hasStructuralChildren: boolean
    isContentHidden?: boolean
    isHidden?: boolean
    parent?: TreeNode
    isAdditional?: boolean
    isLeafGroup?: boolean
}

type HorizontalTreeProps = {
    treeNodes: TreeNode[]
    getProfileFromIdStr: (idStr: string) => Profile | null
    isExpanded: (profileIdStr: string) => boolean
    toggleExpand: (profileIdStr: string) => void
    collapsingProfileIds: Set<string>
    collapsedProfileIds: Set<string>
}

export const HorizontalTree: React.FC<HorizontalTreeProps> = ({
    treeNodes,
    getProfileFromIdStr,
    isExpanded,
    toggleExpand,
    collapsingProfileIds,
    collapsedProfileIds,
}) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [containerSize, setContainerSize] = useState({width: 0, height: 0})
    const recordedShiftByNodeIdRef = useRef<Map<string, {left: number; right: number}>>(new Map())
    const [lockedCanvasSize, setLockedCanvasSize] = useState<{width: number; height: number} | null>(null)
    const toPxY = (rowY: number) => Math.round(rowY * ROW_HEIGHT)
    const isPendingCollapseFade = (node: TreeNode): boolean => {
        if (node.isHidden) return false
        let current = node.parent
        while (current) {
            if (collapsingProfileIds.has(current.profile.idStr)) return true
            current = current.parent
        }
        return false
    }

    // Note: connecting line routing used to be lane-based, but this horizontal org chart
    // now uses fixed row spacing again.

    // Update container size when nodes change
    useEffect(() => {
        if (!containerRef.current || treeNodes.length === 0) {
            setContainerSize({width: 0, height: 0})
            setLockedCanvasSize(null)
            return
        }

        const validNodes = treeNodes.filter((n) => !isNaN(n.x) && !isNaN(n.y) && isFinite(n.x) && isFinite(n.y))

        if (validNodes.length === 0) {
            console.warn('No valid nodes for container size calculation')
            setContainerSize({width: 100, height: 100})
            return
        }

        const maxX = Math.max(...validNodes.map((n) => n.x + PROFILE_WIDTH), 0)
        const maxY = Math.max(...validNodes.map((n) => n.y), 0)

        const width = Math.max(maxX + HORIZONTAL_SPACING, 100)
        const height = Math.max((maxY + 1) * ROW_HEIGHT, 100)

        const measuredSize = {width, height}
        setContainerSize(measuredSize)
        // Lock scroll/canvas extent once per tree load so animation cannot change it.
        setLockedCanvasSize((prev) => prev ?? measuredSize)
    }, [treeNodes])

    const drawLinesForParent = (node: TreeNode, offsetX: number, offsetY: number) => {
        const lines: JSX.Element[] = []
        const childNodes = node.children.filter((c) => nodeById.has(c.profile.idStr))
        if (childNodes.length === 0) return lines

        const parentLevelTop = toPxY(node.y) + LINE_Y_OFFSET - offsetY
        const parentX = node.x + PROFILE_WIDTH / 2 - offsetX
        const parentBottomY = parentLevelTop + PROFILE_HEIGHT

        const shiftedChildCenterX = (child: TreeNode) =>
            child.x + PROFILE_WIDTH / 2 - offsetX + (inwardShiftByNodeId.get(child.profile.idStr) ?? 0)

        const allChildPositions = childNodes.map((child) => {
            const childLevelTop = toPxY(child.y) + LINE_Y_OFFSET - offsetY
            return {
                child,
                x: shiftedChildCenterX(child),
                yTop: childLevelTop,
            }
        })

        if (allChildPositions.length === 0) return lines

        const firstChildTop = Math.min(...allChildPositions.map((p) => p.yTop))
        const rawLeafChildPositions = allChildPositions.filter((p) => !p.child.hasStructuralChildren)
        const rawNonLeafChildPositions = allChildPositions.filter((p) => p.child.hasStructuralChildren)
        const promoteSingleLeaf = rawLeafChildPositions.length === 1
        const leafChildPositions = promoteSingleLeaf ? [] : rawLeafChildPositions
        const nonLeafChildPositions = promoteSingleLeaf
            ? [...rawNonLeafChildPositions, ...rawLeafChildPositions]
            : rawNonLeafChildPositions
        const isMixed = leafChildPositions.length > 0 && nonLeafChildPositions.length > 0

        const rowGap = firstChildTop - parentBottomY
        const verticalDrop = Math.max(4, Math.round(rowGap / 3))
        const firstHorizontalY = parentBottomY + verticalDrop
        const secondHorizontalY = firstChildTop - verticalDrop

        lines.push(
            <line
                key={`parent-vertical-${node.profile.idStr}`}
                x1={parentX}
                y1={parentBottomY - PARENT_LINE_START_ADJUST}
                x2={parentX}
                y2={isMixed ? firstHorizontalY : secondHorizontalY}
                stroke={colors.darkGray}
                strokeWidth={LINE_WIDTH}
            />,
        )

        if (!isMixed) {
            const isLeafOnly = leafChildPositions.length === allChildPositions.length
            const stubLength = 7
            const leftmostX = Math.min(...allChildPositions.map((p) => p.x))
            const rightmostX = Math.max(...allChildPositions.map((p) => p.x))
            lines.push(
                <line
                    key={`bottom-bar-${node.profile.idStr}`}
                    x1={leftmostX}
                    y1={secondHorizontalY}
                    x2={rightmostX}
                    y2={secondHorizontalY}
                    stroke={colors.darkGray}
                    strokeWidth={LINE_WIDTH}
                />,
            )

            if (isLeafOnly && leafChildPositions.length > 0) {
                const leftMostLeaf = leafChildPositions.reduce(
                    (min, p) => (p.x < min.x ? p : min),
                    leafChildPositions[0],
                )
                const rightMostLeaf = leafChildPositions.reduce(
                    (max, p) => (p.x > max.x ? p : max),
                    leafChildPositions[0],
                )
                ;[leftMostLeaf, rightMostLeaf].forEach((ext, i) => {
                    lines.push(
                        <line
                            key={`child-vertical-leaf-stub-${node.profile.idStr}-${i}`}
                            x1={ext.x}
                            y1={secondHorizontalY}
                            x2={ext.x}
                            y2={secondHorizontalY + stubLength}
                            stroke={colors.darkGray}
                            strokeWidth={LINE_WIDTH}
                        />,
                    )
                })
            } else {
                allChildPositions.forEach(({x, yTop}, index) => {
                    lines.push(
                        <line
                            key={`child-vertical-${node.profile.idStr}-${index}`}
                            x1={x}
                            y1={secondHorizontalY}
                            x2={x}
                            y2={yTop + CHILD_LINE_END_ADJUST}
                            stroke={colors.darkGray}
                            strokeWidth={LINE_WIDTH}
                        />,
                    )
                })
            }
        } else {
            const stubLength = 7
            const nonLeafLeft = Math.min(...nonLeafChildPositions.map((p) => p.x))
            const nonLeafRight = Math.max(...nonLeafChildPositions.map((p) => p.x))
            const leafLeft = Math.min(...leafChildPositions.map((p) => p.x))
            const leafRight = Math.max(...leafChildPositions.map((p) => p.x))
            const nonLeafCenterX =
                nonLeafChildPositions.reduce((sum, p) => sum + p.x, 0) / Math.max(1, nonLeafChildPositions.length)
            const leafCenterX =
                leafChildPositions.reduce((sum, p) => sum + p.x, 0) / Math.max(1, leafChildPositions.length)
            const centersCoincide = Math.abs(nonLeafCenterX - leafCenterX) < 0.5

            lines.push(
                <line
                    key={`upper-to-nonleaf-${node.profile.idStr}`}
                    x1={parentX}
                    y1={firstHorizontalY}
                    x2={nonLeafCenterX}
                    y2={firstHorizontalY}
                    stroke={colors.darkGray}
                    strokeWidth={LINE_WIDTH}
                />,
            )
            if (!centersCoincide) {
                lines.push(
                    <line
                        key={`upper-to-leaf-${node.profile.idStr}`}
                        x1={parentX}
                        y1={firstHorizontalY}
                        x2={leafCenterX}
                        y2={firstHorizontalY}
                        stroke={colors.darkGray}
                        strokeWidth={LINE_WIDTH}
                    />,
                )
            }

            lines.push(
                <line
                    key={`upper-nonleaf-to-lower-${node.profile.idStr}`}
                    x1={nonLeafCenterX}
                    y1={firstHorizontalY}
                    x2={nonLeafCenterX}
                    y2={secondHorizontalY}
                    stroke={colors.darkGray}
                    strokeWidth={LINE_WIDTH}
                />,
            )
            if (!centersCoincide) {
                lines.push(
                    <line
                        key={`upper-leaf-to-lower-${node.profile.idStr}`}
                        x1={leafCenterX}
                        y1={firstHorizontalY}
                        x2={leafCenterX}
                        y2={secondHorizontalY}
                        stroke={colors.darkGray}
                        strokeWidth={LINE_WIDTH}
                    />,
                )
            }

            lines.push(
                <line
                    key={`lower-nonleaf-bar-${node.profile.idStr}`}
                    x1={nonLeafLeft}
                    y1={secondHorizontalY}
                    x2={nonLeafRight}
                    y2={secondHorizontalY}
                    stroke={colors.darkGray}
                    strokeWidth={LINE_WIDTH}
                />,
            )
            lines.push(
                <line
                    key={`lower-leaf-bar-${node.profile.idStr}`}
                    x1={leafLeft}
                    y1={secondHorizontalY}
                    x2={leafRight}
                    y2={secondHorizontalY}
                    stroke={colors.darkGray}
                    strokeWidth={LINE_WIDTH}
                />,
            )

            nonLeafChildPositions.forEach(({x, yTop}, index) => {
                lines.push(
                    <line
                        key={`child-vertical-nonleaf-${node.profile.idStr}-${index}`}
                        x1={x}
                        y1={secondHorizontalY}
                        x2={x}
                        y2={yTop + CHILD_LINE_END_ADJUST}
                        stroke={colors.darkGray}
                        strokeWidth={LINE_WIDTH}
                    />,
                )
            })

            if (leafChildPositions.length > 0) {
                const leftMostLeaf = leafChildPositions.reduce(
                    (min, p) => (p.x < min.x ? p : min),
                    leafChildPositions[0],
                )
                const rightMostLeaf = leafChildPositions.reduce(
                    (max, p) => (p.x > max.x ? p : max),
                    leafChildPositions[0],
                )
                ;[leftMostLeaf, rightMostLeaf].forEach((ext, i) => {
                    lines.push(
                        <line
                            key={`leaf-extreme-${node.profile.idStr}-${i}`}
                            x1={ext.x}
                            y1={secondHorizontalY}
                            x2={ext.x}
                            y2={secondHorizontalY + stubLength}
                            stroke={colors.darkGray}
                            strokeWidth={LINE_WIDTH}
                        />,
                    )
                })
            }
        }

        return lines
    }

    const nodeById = new Map(treeNodes.map((n) => [n.profile.idStr, n]))
    const childIdSet = new Set<string>()
    for (const node of treeNodes) {
        for (const ch of node.children) childIdSet.add(ch.profile.idStr)
    }
    const rootNodes = treeNodes.filter((n) => !childIdSet.has(n.profile.idStr))
    const activeShiftIds = new Set<string>([...Array.from(collapsingProfileIds), ...Array.from(collapsedProfileIds)])

    const collectSubtreeIds = (root: TreeNode): Set<string> => {
        const ids = new Set<string>()
        const walk = (n: TreeNode) => {
            ids.add(n.profile.idStr)
            for (const ch of n.children) walk(ch)
        }
        walk(root)
        return ids
    }

    const computeRowGaps = (
        rowExternalNodes: TreeNode[],
        subLeft: number,
        subRight: number,
    ): {leftGap: number; rightGap: number; hasLeftNeighbor: boolean; hasRightNeighbor: boolean} => {
        const leftNeighbor = rowExternalNodes
            .map((n) => n.x + PROFILE_WIDTH)
            .filter((rightEdge) => rightEdge <= subLeft)
            .reduce((max, v) => Math.max(max, v), -Infinity)
        const rightNeighbor = rowExternalNodes
            .map((n) => n.x)
            .filter((leftEdge) => leftEdge >= subRight)
            .reduce((min, v) => Math.min(min, v), Infinity)

        const leftGap = Number.isFinite(leftNeighbor) ? Math.max(0, subLeft - leftNeighbor - HORIZONTAL_SPACING) : 0
        const rightGap = Number.isFinite(rightNeighbor) ? Math.max(0, rightNeighbor - subRight - HORIZONTAL_SPACING) : 0
        return {
            leftGap,
            rightGap,
            hasLeftNeighbor: Number.isFinite(leftNeighbor),
            hasRightNeighbor: Number.isFinite(rightNeighbor),
        }
    }

    const computeRemainingContentGapOnRow = (
        rowExternalNodes: TreeNode[],
        splitX: number,
    ): {remainingGap: number; hasLeftContent: boolean; hasRightContent: boolean} => {
        const leftRightEdge = rowExternalNodes
            .map((n) => n.x + PROFILE_WIDTH)
            .filter((rightEdge) => rightEdge <= splitX)
            .reduce((max, v) => Math.max(max, v), -Infinity)
        const rightLeftEdge = rowExternalNodes
            .map((n) => n.x)
            .filter((leftEdge) => leftEdge >= splitX)
            .reduce((min, v) => Math.min(min, v), Infinity)

        const hasLeftContent = Number.isFinite(leftRightEdge)
        const hasRightContent = Number.isFinite(rightLeftEdge)
        const remainingGap =
            hasLeftContent && hasRightContent
                ? Math.max(0, rightLeftEdge - leftRightEdge - HORIZONTAL_SPACING)
                : LARGE_GAP
        return {remainingGap, hasLeftContent, hasRightContent}
    }

    const verticalIntervalsOverlap = (aTop: number, aBottom: number, bTop: number, bBottom: number): boolean =>
        aTop < bBottom && bTop < aBottom

    const computeDynamicShiftForNode = (collapsingNode: TreeNode): {left: number; right: number} => {
        const structuralNodes = treeNodes
        const subtreeIds = collectSubtreeIds(collapsingNode)
        const subtreeStructuralNodes = structuralNodes.filter((n) => subtreeIds.has(n.profile.idStr))
        const structuralDescendants = subtreeStructuralNodes.filter(
            (n) => n.profile.idStr !== collapsingNode.profile.idStr,
        )
        if (subtreeStructuralNodes.length === 0) {
            // Debug: no subtree content to measure.
            // eslint-disable-next-line no-console
            console.log('[HorizontalTree] dynamicShift', collapsingNode.profile.idStr, {
                reason: 'no-subtree-structural',
                left: FALLBACK_SHIFT,
                right: FALLBACK_SHIFT,
            })
            return {left: FALLBACK_SHIFT, right: FALLBACK_SHIFT}
        }
        if (structuralDescendants.length === 0) {
            // No descendants => no gap to reclaim.
            // eslint-disable-next-line no-console
            console.log('[HorizontalTree] dynamicShift', collapsingNode.profile.idStr, {
                reason: 'no-structural-descendants',
                selectedLeft: 0,
                selectedRight: 0,
            })
            return {left: 0, right: 0}
        }

        const rows = Array.from(new Set(subtreeStructuralNodes.map((n) => toPxY(n.y))))
        const collapsingRowPx = toPxY(collapsingNode.y)
        const sortedRows = rows.sort((a, b) => a - b)
        let minCombined = LARGE_GAP
        let subtreeContentWidth = PROFILE_WIDTH
        let collapsingRowLeftGap = 0
        let collapsingRowRightGap = 0
        const getAnchorCenterX = (n: TreeNode) => {
            const immediateChildren = n.children.filter((c) => nodeById.has(c.profile.idStr))
            if (immediateChildren.length === 0) return n.x + PROFILE_WIDTH / 2
            const nonLeaf = immediateChildren.filter((c) => c.hasStructuralChildren)
            const leaf = immediateChildren.filter((c) => !c.hasStructuralChildren)
            if (nonLeaf.length > 0 && leaf.length > 0) {
                const nonLeafCenter = nonLeaf.reduce((sum, c) => sum + c.x + PROFILE_WIDTH / 2, 0) / nonLeaf.length
                const leafCenter = leaf.reduce((sum, c) => sum + c.x + PROFILE_WIDTH / 2, 0) / leaf.length
                return (nonLeafCenter + leafCenter) / 2
            }
            const left = Math.min(...immediateChildren.map((c) => c.x))
            const right = Math.max(...immediateChildren.map((c) => c.x + PROFILE_WIDTH))
            return (left + right) / 2
        }
        const collapsingCenterX = getAnchorCenterX(collapsingNode)
        const rowDebug: Array<{
            rowOffset: number
            rowPx: number
            leftGap: number
            rightGap: number
            hasLeftNeighbor: boolean
            hasRightNeighbor: boolean
            remainingGap: number
            hasLeftContent: boolean
            hasRightContent: boolean
            subLeft: number
            subRight: number
        }> = []

        for (const rowPx of sortedRows) {
            const rowSubtreeNodes = subtreeStructuralNodes.filter((n) => toPxY(n.y) === rowPx)
            if (rowSubtreeNodes.length === 0) continue

            const subLeft = rowPx === collapsingRowPx ? collapsingNode.x : Math.min(...rowSubtreeNodes.map((n) => n.x))
            const subRight =
                rowPx === collapsingRowPx
                    ? collapsingNode.x + PROFILE_WIDTH
                    : Math.max(...rowSubtreeNodes.map((n) => n.x + PROFILE_WIDTH))

            subtreeContentWidth = Math.max(subtreeContentWidth, subRight - subLeft)

            const bandTop = rowPx
            const bandBottom = rowPx + PROFILE_HEIGHT
            const rowExternalNodes = structuralNodes.filter((n) => {
                if (subtreeIds.has(n.profile.idStr)) return false
                const nodeTop = toPxY(n.y)
                const nodeBottom = nodeTop + PROFILE_HEIGHT
                if (rowPx === collapsingRowPx) return nodeTop === rowPx
                return verticalIntervalsOverlap(nodeTop, nodeBottom, bandTop, bandBottom)
            })
            const {leftGap, rightGap, hasLeftNeighbor, hasRightNeighbor} = computeRowGaps(
                rowExternalNodes,
                subLeft,
                subRight,
            )
            const {remainingGap, hasLeftContent, hasRightContent} = computeRemainingContentGapOnRow(
                rowExternalNodes,
                collapsingCenterX,
            )
            const effectiveLeftGap = rowPx === collapsingRowPx ? leftGap / 2 : leftGap
            const effectiveRightGap = rowPx === collapsingRowPx ? rightGap / 2 : rightGap
            const combined = rowPx === collapsingRowPx ? effectiveLeftGap + effectiveRightGap : remainingGap
            minCombined = Math.min(minCombined, combined)
            rowDebug.push({
                rowOffset: Math.round((rowPx - collapsingRowPx) / ROW_HEIGHT),
                rowPx,
                leftGap: effectiveLeftGap,
                rightGap: effectiveRightGap,
                hasLeftNeighbor,
                hasRightNeighbor,
                remainingGap,
                hasLeftContent,
                hasRightContent,
                subLeft,
                subRight,
            })

            if (rowPx === collapsingRowPx) {
                collapsingRowLeftGap = effectiveLeftGap
                collapsingRowRightGap = effectiveRightGap
            }
        }

        // Do not cap by (subtreeContentWidth - PROFILE_WIDTH):
        // from row 1 onwards the full disappearing subtree width is reclaimable.
        // Row 0 constraints are already included through row-specific left/right capacities.
        const maxCombinedShift = Math.max(0, Number.isFinite(minCombined) ? minCombined : 0)
        if (maxCombinedShift <= 0) {
            // eslint-disable-next-line no-console
            console.log('[HorizontalTree] dynamicShift', collapsingNode.profile.idStr, {
                rows: rowDebug,
                subtreeContentWidth,
                maxCombinedShift,
                selectedLeft: 0,
                selectedRight: 0,
            })
            return {left: 0, right: 0}
        }

        let left = 0
        let right = 0
        const shiftStep = PROFILE_WIDTH + HORIZONTAL_SPACING
        const totalCapacity = Math.max(0, collapsingRowLeftGap) + Math.max(0, collapsingRowRightGap)
        const totalStepBudget = Math.floor(maxCombinedShift / shiftStep)

        // Prefer whole "block + gap" movements and bias toward the side with more available space.
        if (totalStepBudget > 0 && totalCapacity > 0) {
            const leftCapSteps = Math.floor(Math.max(0, collapsingRowLeftGap) / shiftStep)
            const rightCapSteps = Math.floor(Math.max(0, collapsingRowRightGap) / shiftStep)
            const feasibleStepBudget = Math.min(totalStepBudget, leftCapSteps + rightCapSteps)
            const minRight = Math.max(0, feasibleStepBudget - leftCapSteps)
            const maxRight = Math.min(feasibleStepBudget, rightCapSteps)
            let rightSteps = maxRight
            let bestDiff = Number.POSITIVE_INFINITY
            for (let candidateRight = minRight; candidateRight <= maxRight; candidateRight += 1) {
                const candidateLeft = feasibleStepBudget - candidateRight
                if (candidateLeft > leftCapSteps) continue
                const leftRatio = leftCapSteps > 0 ? candidateLeft / leftCapSteps : 1
                const rightRatio = rightCapSteps > 0 ? candidateRight / rightCapSteps : 1
                const diff = Math.abs(leftRatio - rightRatio)
                // Tie-breaker prefers more right movement to avoid underutilized right space.
                if (diff < bestDiff - 1e-6 || (Math.abs(diff - bestDiff) < 1e-6 && candidateRight > rightSteps)) {
                    bestDiff = diff
                    rightSteps = candidateRight
                }
            }
            const leftSteps = feasibleStepBudget - rightSteps

            left = leftSteps * shiftStep
            right = rightSteps * shiftStep
        } else if (totalCapacity > 0) {
            // Pixel-precision fallback for small budgets (< one full step).
            left = (maxCombinedShift * Math.max(0, collapsingRowLeftGap)) / totalCapacity
            right = maxCombinedShift - left
            left = Math.min(left, Math.max(0, collapsingRowLeftGap))
            right = Math.min(right, Math.max(0, collapsingRowRightGap))
        }

        // Allocate remaining pixels (from floor step budget / precision / clamping) to available side(s).
        let remaining = maxCombinedShift - (left + right)
        if (remaining > 0) {
            const leftSpare = Math.max(0, collapsingRowLeftGap - left)
            const rightSpare = Math.max(0, collapsingRowRightGap - right)
            if (leftSpare >= rightSpare) {
                const addLeft = Math.min(leftSpare, remaining)
                left += addLeft
                remaining -= addLeft
                if (remaining > 0) right += Math.min(Math.max(0, collapsingRowRightGap - right), remaining)
            } else {
                const addRight = Math.min(rightSpare, remaining)
                right += addRight
                remaining -= addRight
                if (remaining > 0) left += Math.min(Math.max(0, collapsingRowLeftGap - left), remaining)
            }
        }

        // Hard safety: never exceed row-zero side capacities.
        left = Math.min(left, Math.max(0, collapsingRowLeftGap))
        right = Math.min(right, Math.max(0, collapsingRowRightGap))

        const selectedLeft = Math.round(left)
        const selectedRight = Math.round(right)
        // eslint-disable-next-line no-console
        console.log('[HorizontalTree] dynamicShift', collapsingNode.profile.idStr, {
            rows: rowDebug,
            subtreeContentWidth,
            maxCombinedShift,
            shiftStep,
            collapsingRowLeftGap,
            collapsingRowRightGap,
            selectedLeft,
            selectedRight,
        })
        return {left: selectedLeft, right: selectedRight}
    }

    type Bounds = {minX: number; minY: number; maxX: number; maxY: number}
    const subtreeBoundsById = new Map<string, Bounds>()
    const computeSubtreeBounds = (node: TreeNode): Bounds | null => {
        const isNodePresent = nodeById.has(node.profile.idStr)
        if (!isNodePresent) return null

        const nodeLeft = node.x
        const nodeTop = toPxY(node.y)
        let minX = nodeLeft
        let minY = nodeTop
        let maxX = nodeLeft + PROFILE_WIDTH
        let maxY = nodeTop + PROFILE_HEIGHT

        const childrenForBounds = node.children.filter((c) => nodeById.has(c.profile.idStr))
        for (const child of childrenForBounds) {
            const childBounds = computeSubtreeBounds(child)
            if (!childBounds) continue
            minX = Math.min(minX, childBounds.minX)
            minY = Math.min(minY, childBounds.minY)
            maxX = Math.max(maxX, childBounds.maxX)
            maxY = Math.max(maxY, childBounds.maxY)
        }

        const bounds = {minX, minY, maxX, maxY}
        subtreeBoundsById.set(node.profile.idStr, bounds)
        return bounds
    }

    for (const root of rootNodes) {
        computeSubtreeBounds(root)
    }

    const inwardShiftByNodeId = new Map<string, number>()
    const addInwardShift = (nodeId: string, deltaX: number) => {
        if (Math.abs(deltaX) < 1e-6) return
        inwardShiftByNodeId.set(nodeId, (inwardShiftByNodeId.get(nodeId) ?? 0) + deltaX)
    }
    const baseCenterX = (n: TreeNode) => n.x + PROFILE_WIDTH / 2
    const anchorCenterX = (n: TreeNode) => {
        const immediateChildren = n.children.filter((c) => nodeById.has(c.profile.idStr))
        if (immediateChildren.length === 0) return baseCenterX(n)
        const nonLeaf = immediateChildren.filter((c) => c.hasStructuralChildren)
        const leaf = immediateChildren.filter((c) => !c.hasStructuralChildren)
        if (nonLeaf.length > 0 && leaf.length > 0) {
            const nonLeafCenter = nonLeaf.reduce((sum, c) => sum + c.x + PROFILE_WIDTH / 2, 0) / nonLeaf.length
            const leafCenter = leaf.reduce((sum, c) => sum + c.x + PROFILE_WIDTH / 2, 0) / leaf.length
            return (nonLeafCenter + leafCenter) / 2
        }
        const left = Math.min(...immediateChildren.map((c) => c.x))
        const right = Math.max(...immediateChildren.map((c) => c.x + PROFILE_WIDTH))
        return (left + right) / 2
    }

    const shiftSiblingsTowardNode = (
        targetNode: TreeNode,
        leftShift: number,
        rightShift: number,
        shiftedInCascade?: Set<string>,
    ) => {
        const siblings = targetNode.parent
            ? targetNode.parent.children.filter((s) => !s.isHidden && s.profile.idStr !== targetNode.profile.idStr)
            : rootNodes.filter((s) => !s.isHidden && s.profile.idStr !== targetNode.profile.idStr)

        const targetCenter = anchorCenterX(targetNode)
        const leftSiblings = siblings.filter((s) => anchorCenterX(s) < targetCenter)
        const rightSiblings = siblings.filter((s) => anchorCenterX(s) > targetCenter)

        const shiftOnce = (nodeId: string, deltaX: number) => {
            if (shiftedInCascade && shiftedInCascade.has(nodeId)) return
            addInwardShift(nodeId, deltaX)
            shiftedInCascade?.add(nodeId)
        }

        // Primary behavior: all immediate siblings shift inward.
        for (const s of leftSiblings) shiftOnce(s.profile.idStr, +leftShift)
        for (const s of rightSiblings) shiftOnce(s.profile.idStr, -rightShift)
    }

    // Collapse cascade (hardcoded shifts for now):
    // 1) collapsing node siblings shift in
    // 2) parent siblings shift in
    // 3) repeat up ancestor chain to root (root handled by same helper).
    for (const nodeId of Array.from(recordedShiftByNodeIdRef.current.keys())) {
        if (!activeShiftIds.has(nodeId)) recordedShiftByNodeIdRef.current.delete(nodeId)
    }

    for (const activeId of activeShiftIds) {
        const activeNode = nodeById.get(activeId)
        if (!activeNode || activeNode.isHidden) continue

        if (collapsingProfileIds.has(activeId) && !recordedShiftByNodeIdRef.current.has(activeId)) {
            recordedShiftByNodeIdRef.current.set(activeId, computeDynamicShiftForNode(activeNode))
        }
        const recordedShift = recordedShiftByNodeIdRef.current.get(activeId) ?? {
            left: FALLBACK_SHIFT,
            right: FALLBACK_SHIFT,
        }

        let current: TreeNode | undefined = activeNode
        let isFirstLevel = true
        const shiftedInCascade = new Set<string>()
        while (current?.parent) {
            if (isFirstLevel) {
                shiftSiblingsTowardNode(current, recordedShift.left, recordedShift.right, shiftedInCascade)
                isFirstLevel = false
            }

            const parent: TreeNode = current.parent
            shiftSiblingsTowardNode(parent, recordedShift.left, recordedShift.right, shiftedInCascade)
            current = parent
        }

        if (current && !current.parent) {
            shiftSiblingsTowardNode(current, recordedShift.left, recordedShift.right, shiftedInCascade)
        }
    }

    const renderSubtree = (node: TreeNode, parentMinX: number, parentMinY: number): React.ReactNode => {
        const isPendingFade = isPendingCollapseFade(node)
        const isCardVisible = !node.isHidden && !node.isContentHidden && !isPendingFade
        const subtreeBounds = subtreeBoundsById.get(node.profile.idStr)
        if (!subtreeBounds) return null

        const nodeTopPx = toPxY(node.y)
        const hasChildren = node.children.some((c) => nodeById.has(c.profile.idStr))
        const isSelfCollapsing = collapsingProfileIds.has(node.profile.idStr)
        const isSelfExpanded = isExpanded(node.profile.idStr)
        const areLinesVisible =
            !node.isContentHidden && hasChildren && !isPendingFade && !isSelfCollapsing && isSelfExpanded
        const childNodes = node.children.filter((c) => nodeById.has(c.profile.idStr))
        const subtreeWidth = Math.max(1, subtreeBounds.maxX - subtreeBounds.minX)
        const subtreeHeight = Math.max(1, subtreeBounds.maxY - subtreeBounds.minY)

        const baseLeft = subtreeBounds.minX - parentMinX
        const inwardShift = inwardShiftByNodeId.get(node.profile.idStr) ?? 0
        const translateX = inwardShift
        return (
            <motion.div
                key={node.profile.idStr}
                style={{
                    position: 'absolute',
                    left: `${baseLeft}px`,
                    top: `${subtreeBounds.minY - parentMinY}px`,
                    width: `${subtreeWidth}px`,
                    // Keep container shallow; descendants/lines can paint outside.
                    height: `${PROFILE_HEIGHT}px`,
                    overflow: 'visible',
                }}
                initial={false}
                animate={{left: baseLeft + translateX}}
                transition={{duration: FADE_DURATION_SECONDS}}
            >
                <motion.svg
                    className={LinesContainer.styledComponentId}
                    viewBox={`0 0 ${subtreeWidth} ${subtreeHeight}`}
                    style={{width: `${subtreeWidth}px`, height: `${subtreeHeight}px`, overflow: 'visible'}}
                    initial={false}
                    animate={{opacity: areLinesVisible ? 1 : 0}}
                    transition={{duration: FADE_DURATION_SECONDS}}
                >
                    {drawLinesForParent(node, subtreeBounds.minX, subtreeBounds.minY)}
                </motion.svg>
                <motion.div
                    style={{
                        position: 'absolute',
                        left: `${node.x - subtreeBounds.minX}px`,
                        top: `${nodeTopPx - subtreeBounds.minY}px`,
                        zIndex: (node.y + 1) * 10,
                        pointerEvents: isCardVisible ? 'auto' : 'none',
                    }}
                    initial={false}
                    animate={{opacity: isCardVisible ? 1 : 0}}
                    transition={{duration: FADE_DURATION_SECONDS}}
                >
                    <HorizontalProfileItem
                        profile={node.profile}
                        getProfileFromIdStr={getProfileFromIdStr}
                        isExpanded={isExpanded(node.profile.idStr)}
                        toggleExpand={() => toggleExpand(node.profile.idStr)}
                        setRef={() => {}}
                    />
                </motion.div>
                {childNodes.map((child) => {
                    return renderSubtree(child, subtreeBounds.minX, subtreeBounds.minY)
                })}
            </motion.div>
        )
    }

    if (treeNodes.length === 0) {
        return (
            <TreeContainer ref={containerRef} style={{padding: '20px'}}>
                <div>No profiles to display. TreeNodes: {treeNodes.length}</div>
            </TreeContainer>
        )
    }

    // Debug: log node positions
    if (process.env.NODE_ENV === 'development') {
        console.log('Rendering treeNodes:', treeNodes.length, 'Container size:', containerSize)
        if (treeNodes.length > 0) {
            console.log('First node:', treeNodes[0].profile.idStr, 'Position:', treeNodes[0].x, treeNodes[0].y)
        }
    }

    const renderSize = lockedCanvasSize ?? containerSize

    return (
        <TreeContainer ref={containerRef} style={{width: renderSize.width, height: renderSize.height}}>
            {rootNodes.map((root) => renderSubtree(root, 0, 0))}
        </TreeContainer>
    )
}
